import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components/ui/card';
import { Button } from '@/ui/components/ui/button';
import { useAppContext, Project } from '@/store/AppContext';
import { CheckCircle2, FileText, HardHat, Loader2, Calendar, MapPin, Camera, Eye } from 'lucide-react';
import { Logo } from '../components/Logo';

export function PublicQuote() {
  const { getProjectByToken, acceptProjectAsClient } = useAppContext();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    const token = path.split('/').pop();
    if (token && path.includes('/quote/')) {
      getProjectByToken(token).then(p => {
        setProject(p);
        setLoading(false);
        if (p?.status === 'accepted') setAccepted(true);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const handleAccept = async () => {
    if (!project) return;
    const token = window.location.pathname.split('/').pop() || '';
    try {
      await acceptProjectAsClient(project.id, token);
      setAccepted(true);
    } catch (error) {
      alert("Wystąpił błąd podczas akceptacji wyceny.");
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 p-4">
        <div className="text-center space-y-4 max-w-md">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
            <h1 className="text-2xl font-bold text-slate-900">Wycena nie znaleziona</h1>
            <p className="text-slate-500 mt-2">Link może być nieaktywny lub wygasł. Skontaktuj się z wykonawcą.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <Logo variant="dark" />
          <div className="text-right">
            <p className="text-sm text-slate-500">Wycena nr: {project.id.substring(0, 8)}</p>
            <p className="text-sm text-slate-500">Data: {project.date}</p>
          </div>
        </div>

        {accepted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex items-center gap-4">
            <div className="bg-emerald-100 p-3 rounded-full">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-emerald-900">Wycena Zaakceptowana!</h4>
              <p className="text-emerald-700 text-sm">Dziękujemy za akceptację. Wykonawca został powiadomiony i wkrótce się z Tobą skontaktuje.</p>
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-2xl font-bold text-slate-900">{project.name}</CardTitle>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {project.location || 'Polska'}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" /> Termin: {project.deadline || 'Do ustalenia'}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Zakres prac</h3>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{project.details}</p>
              </div>

              {project.images && project.images.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Camera className="w-5 h-5 text-slate-400" /> Zdjęcia obiektu
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {project.images.map((img, idx) => (
                      <div key={idx} className="aspect-square rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                        <img src={img} alt={`Zdjęcie ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {project.beforeAfterPhotos && project.beforeAfterPhotos.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Eye className="w-5 h-5 text-blue-600" /> Porównanie Przed i Po
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {project.beforeAfterPhotos.map((pair, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-2xl p-4 border border-slate-100 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase">Przed</p>
                            <div className="aspect-square rounded-lg overflow-hidden bg-slate-200">
                              {pair.beforeUrl && <img src={pair.beforeUrl} className="w-full h-full object-cover" />}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase">Po</p>
                            <div className="aspect-square rounded-lg overflow-hidden bg-slate-200">
                              {pair.afterUrl && <img src={pair.afterUrl} className="w-full h-full object-cover" />}
                            </div>
                          </div>
                        </div>
                        {pair.comment && <p className="text-xs text-slate-600 italic">{pair.comment}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Zestawienie kosztów</h3>
                <div className="border border-slate-100 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Pozycja</th>
                        <th className="px-4 py-3 text-right">Wartość</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="px-4 py-3 text-slate-700">Materiały budowlane</td>
                        <td className="px-4 py-3 text-right font-medium">{(project.totalBaseCost || 0).toFixed(2)} PLN</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-slate-700">Materiały pomocnicze</td>
                        <td className="px-4 py-3 text-right text-slate-900 font-medium">{(project.auxiliaryMaterialsCost || 0).toFixed(2)} PLN</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-slate-700">Robocizna i montaż</td>
                        <td className="px-4 py-3 text-right text-slate-900 font-medium">{(project.laborCost || 0).toFixed(2)} PLN</td>
                      </tr>
                      {(project.transportCost || 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-slate-700">Transport i logistyka</td>
                        <td className="px-4 py-3 text-right text-slate-900 font-medium">{(project.transportCost || 0).toFixed(2)} PLN</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="bg-slate-50 font-bold">
                      <tr>
                        <td className="px-4 py-3 text-slate-900">Suma Netto</td>
                        <td className="px-4 py-3 text-right text-slate-900 font-medium">{((project.totalBaseCost || 0) + (project.auxiliaryMaterialsCost || 0) + (project.laborCost || 0) + (project.transportCost || 0)).toFixed(2)} PLN</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="bg-slate-900 text-white p-8 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <p className="text-slate-400 text-sm uppercase tracking-wider font-semibold">Całkowity koszt brutto</p>
                  <p className="text-4xl font-bold mt-1">
                    {(((project.totalBaseCost || 0) + (project.auxiliaryMaterialsCost || 0) + (project.laborCost || 0) + (project.transportCost || 0)) * (1 + (project.vatRate || 8) / 100)).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN
                  </p>
                  <p className="text-emerald-400/80 text-lg font-medium mt-1">
                    ± {(((project.totalBaseCost || 0) + (project.auxiliaryMaterialsCost || 0) + (project.laborCost || 0) + (project.transportCost || 0)) * (1 + (project.vatRate || 8) / 100) * 0.1).toLocaleString('pl-PL', { maximumFractionDigits: 0 })} PLN (margines 10%)
                  </p>
                  <p className="text-slate-400 text-xs mt-2">* Wycena zawiera podatek VAT {project.vatRate || 8}%</p>
                </div>
                {!accepted && (
                  <Button onClick={handleAccept} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-xl gap-2 shadow-lg shadow-emerald-500/20">
                    <CheckCircle2 className="w-6 h-6" /> Akceptuję wycenę
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center py-8 border-t border-slate-200">
          <p className="text-slate-400 text-sm">Wygenerowano w ProWycena — wycena robocza do potwierdzenia</p>
        </div>
      </div>
    </div>
  );
}
